import React from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { SocialButton } from './SocialButton';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { IDPConnection } from '../../types';
import { IDP_APPDEFIDS, ForcedState } from '../../../constants';
import { getProviderColorTheme } from './providerThemeConfig';
import { resolveServerErrorMessage } from '../../utils/errorHandler';
import { classes } from './SocialButtonsSection.st.css';

export const SocialButtonsSection = ({
  enabledConnections,
  loginWithIdp,
  setError,
  isEditor,
  isPreview,
  layoutParams,
  settingsParams,
  stylesParams,
  forceView,
  autoFocus = false,
}: {
  enabledConnections: IDPConnection[];
  loginWithIdp: (
    idpConnectionId: string,
    joinCommunity?: boolean,
  ) => Promise<unknown>;
  setError: (error?: string) => void;
  isEditor: boolean;
  isPreview?: boolean;
  layoutParams: any;
  settingsParams: any;
  stylesParams: any;
  forceView?: ForcedState;
  autoFocus?: boolean;
}) => {
  const styles = useStyles();
  const settings = useSettings();
  const { t } = useTranslation();

  // Social login is a redirect/popup flow: members.authentication.loginWithIdp
  // resolves/rejects after the IDP popup posts its result back. Mirror the
  // email/password handling (see LoginFormSection) so a server error code such
  // as NO_EMAIL_WAS_PROVIDED_FROM_IDP resolves to its specific message instead
  // of being dropped and falling back to the generic one.
  const handleSocialLogin = async (connection: IDPConnection) => {
    // Pre-open the popup synchronously on click to avoid iOS popup blocking (#544).
    window.open('about:blank', 'oauthPopup', 'width=450,height=522');
    setError(undefined);
    try {
      await loginWithIdp(connection.id);
    } catch (error) {
      setError(
        resolveServerErrorMessage(error as any, t, {
          provider: connection.displayName,
        }),
      );
    }
  };

  const shouldShowSocialButton = (appDefId: string): boolean => {
    return enabledConnections.some((conn) => conn.appDefId === appDefId);
  };
  const buttonConfigs: any = {
    google: {
      appDefId: IDP_APPDEFIDS.google,
      shouldBeShown: shouldShowSocialButton(IDP_APPDEFIDS.google),
    },
    facebook: {
      appDefId: IDP_APPDEFIDS.facebook,
      shouldBeShown: shouldShowSocialButton(IDP_APPDEFIDS.facebook),
    },
    sso: {
      appDefId: IDP_APPDEFIDS.sso,
      shouldBeShown: true,
    },
  };
  const getSocialConnectionsToRender = () => {
    const socialButtonsOrder = settings.get(settingsParams.socialButtonsOrder);
    const connections = [];
    for (const buttonType of socialButtonsOrder) {
      const buttonConfig = buttonConfigs[buttonType];
      if (buttonConfig.shouldBeShown) {
        const connection = enabledConnections.find(
          (conn) => conn.appDefId === buttonConfig.appDefId,
        );
        if (connection) {
          connections.push(connection);
        }
      }
    }

    // TODO: significantly refactor this logic. Ideally, the order that's saved in the settings
    // will define the priority only, while the list of possible elements will be defined
    // elsewhere or derived from other data correctly
    const ssoConnection = enabledConnections.find(
      (conn) => conn.appDefId === IDP_APPDEFIDS.sso,
    );
    const ssoAlreadyInList = connections.some(
      (conn) => conn.appDefId === IDP_APPDEFIDS.sso,
    );

    if (ssoConnection && !ssoAlreadyInList) {
      connections.push(ssoConnection);
    }

    return connections;
  };
  const connectionsToRender = getSocialConnectionsToRender();

  return (
    <div className={classes.socialButtonsContainer}>
      {connectionsToRender.map((connection, index) => (
        <div key={connection.id} className={classes.socialButtonWrapper}>
          <SocialButton
            idpConnection={connection}
            theme={getProviderColorTheme(
              connection.appDefId,
              styles,
              stylesParams,
            )}
            onClick={() => handleSocialLogin(connection)}
            isEditor={isEditor}
            isPreview={isPreview}
            layoutParams={layoutParams}
            settingsParams={settingsParams}
            stylesParams={stylesParams}
            forceView={forceView}
            autoFocus={autoFocus && index === 0}
          />
        </div>
      ))}
    </div>
  );
};
